// 📁 src/pages/Sondage.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import "./Sondage.css";
import { Paper, Box, Typography, useMediaQuery, Button, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit"; 
import { GridEditInputCell } from "@mui/x-data-grid";

import HeaderGroupe from "./HeaderGroupe.js";
import BoutonEnvoyerEmailSondage from "./BoutonEnvoyerEmailSondage.js";

import { useGroupe } from "../context/GroupeContext.js";
import { useLanguage } from '../context/LanguageContext.js';

import { formatContact } from "../utils/formatContact.js";
import { formatDateFr } from "../utils/date.js";
import { logDev } from "../utils/logger.js";
import { calculateTotal } from "../utils/periodeDominante.js";
import { createProcessRowUpdate } from "../utils/createProcessRowUpdate.js";
import { getDominantPeriodes } from "../utils/periodeUtils.js";
import { extractValidEmails } from "../utils/contactUtils.js";

import TableauSondage from "../components/TableauSondage.js";
import BoutonWhatsApp from "../components/BoutonWhatsApp.js";
import FormulairePeriodes from "../components/FormulairePeriods.js";
import BoutonModifierPeriodes from "../components/BoutonModifierPeriodes.js";
import ChoixPeriodeDominante from "../components/ChoixPeriodeDominante.js";
import TableauListeItems from "../components/ListItems/TableauListeItems.js";
import BoutonCopierTousEmails from "../components/BoutonCopierTousEmails.js";
import BoutonEmailPeriodeDominante from "../components/BoutonEmailPeriodeDominante.js";
import BoutonEnvoyerSMSParticipants from "../components/BoutonEnvoyerSMSParticipants.js";
import BoutonValiderEtVerrouiller from "../components/BoutonValiderEtVerrouiller.js";
import MenusJournalier from "../components/MenusJournalier/MenusJournalier.js";
import BoutonPartagerSondageWhatsApp from "../components/BoutonPartagerSondageWhatsApp.js";

import { useContactsPeriodeDominante } from "../hooks/useContactsPeriodeDominante.js";
import { usePeriodeDominante } from "../hooks/usePeriodeDominante.js";
import { useAdminRole } from "../hooks/useAdminRole.js";
import { useBottomNavHeight } from "../hooks/useBottomNavHeight.js";

import SnackbarFeedback from "../pages/SnackbarFeedback.js";


const Sondage = ({ headerHeight, periodes, onReloadPériodes }) => {
  const { t, locale } = useLanguage();
  const [contacts, setContacts] = useState([]);
  const { nom: nomGroupe, groupeId, groupeActif, setGroupeActif, reloadGroupeActif } = useGroupe();
  const safePeriodes = Array.isArray(periodes) ? periodes : [];
  const [ editionActive, setEditionActive ] = useState(false);
  const noPeriode = safePeriodes.length === 0;
  const [ itemsMenus, setItemsMenus ] = useState({});
  const { emoji, couleur } = useGroupe();
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const bottomNavHeight = useBottomNavHeight();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
    duration: 3000
  });

  const role = useAdminRole(groupeId);
  const isAdmin = role === "admin";
  const navigate = useNavigate();

  const { dominantKeys, periodeDominanteEffective, periodePrivilegiee, setPeriodePrivilegiee, totals } =
  usePeriodeDominante(contacts, safePeriodes);

  const [periodeValidee, setPeriodeValidee] = useState(null); // ce qui est validé côté back
  const isSurveyClosed = !!periodeValidee;

  const hasVotes = safePeriodes.some((periode) =>
  calculateTotal(contacts, periode._id) > 0
  );

  // Hook qui calcule déjà la période dominante
  const winningKey = periodeDominanteEffective || (safePeriodes[0]?._id ?? null);

  const { contactsDominante, emailsDominante, info, loading } = useContactsPeriodeDominante(contacts, {
    winningKey, // clé calculée par usePeriodeDominante
    onlyValidEmails: true,
    fallbackAll: true
  });

  logDev("📬 Vérification des contactsDominante pour emailsPeriodeDominante", {
    periodeDominanteEffective,
    contactsDominante
  });

  const emailsPeriodeDominante = extractValidEmails(contactsDominante);
  
  logDev("[Sondage] hook useContactsPeriodeDominante", { contactsDominante, emailsDominante, info, loading });

  const rechargerContacts = async () => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API}/contacts?groupe=${groupeId}`
    );
    
    const contactsArray = Array.isArray(response.data)
      ? response.data
      : [];

      setContacts(
        contactsArray.map(c => formatContact(c, safePeriodes))
      );
      
      logDev("✅ Contacts rechargés après participation");
      } catch (err) {
        logDev("❌ Erreur rechargement contacts", {
          message: err.message,
          stack: err.stack
        });
      }
    };

    const processRowUpdate = useMemo(() => {
      if (!groupeId) return () => {}; // fonction neutre si pas de groupe
        return createProcessRowUpdate(groupeId, safePeriodes, setContacts, locale);
      }, [groupeId, safePeriodes, locale]);

      const isMobile = useMediaQuery("(max-width:600px)") || /Mobi|Android/i.test(navigator.userAgent);

      const handleVerrouillage = async () => {
        try {
          logDev("🔐 Tentative de verrouillage sondage…");
          await axios.put(`${process.env.REACT_APP_API}/groupes-admin/${groupeId}/verrouiller-sondage`);
          logDev("✅ Verrouillage réussi");

          // 🔁 Recharge le groupe pour obtenir sondageVerrouille à jour
          const updated = await axios.get(`${process.env.REACT_APP_API}/groupes/${groupeId}`);
          logDev("✅ Groupe mis à jour après verrouillage", updated.data);
          setGroupeActif(updated.data);

          setSnackbar({
            open: true,
            message: t("SurveyLocked"),
            severity: "success"
          });

          reloadGroupeActif(); // si tu veux recharger le contexte

          } catch (err) {

            logDev("❌ Erreur verrouillage sondage", { 
              message: err.message,
              status: err.response?.status,
              data: err.response?.data,
              groupeId,
              url: `${process.env.REACT_APP_API}/groupes/${groupeId}/verrouiller-sondage`
            });

            setSnackbar({
              open: true,
              message: t("ErrorLockingSurvey"),
              severity: "error"
            });
        }
    };

    useEffect(() => {
      if (safePeriodes.length === 0 && isAdmin) {
        logDev("[Sondage] safePeriodes vide → reload demandé");
        onReloadPériodes();
      }
    }, []);

    useEffect(() => {
      axios.get(`${process.env.REACT_APP_API}/contacts?groupe=${groupeId}`)
        .then((response) => {
          logDev("[Sondage] contacts reçus", response.data);
          const contactsArray = Array.isArray(response.data) ? response.data : [];
          setContacts(
            contactsArray.map(c => formatContact(c, safePeriodes))
          );     
        })
        .catch((err) => logDev("❌ Erreur chargement contacts", { message: err.message, stack: err.stack }));
        }, [groupeId, safePeriodes]);

        useEffect(() => {

          axios.get(`${process.env.REACT_APP_API}/groupes/${groupeId}`)
          .then(res => {
            logDev("[Sondage] groupe reçu", res.data);


            if (res.data.periodeValidee) {
              setPeriodeValidee(res.data.periodeValidee);
              }

              if (res.data.periodePrivilegiee) {
                setPeriodePrivilegiee(res.data.periodePrivilegiee);
              }

              if (res.data.notifierRemplissageSondage !== undefined) {
                setNotifierRemplissage(res.data.notifierRemplissageSondage);
              }
              })

              .catch((err) => logDev("❌ Erreur chargement groupe", { message: err.message, stack: err.stack }));
            }, [groupeId]);


            if (loading) return <div>Chargement…</div>;
            logDev("👉 Totaux calculés par calculateTotal:",
              safePeriodes.map((periode) => calculateTotal(contacts, periode._id))
              );
              
              const destinataires = contacts.map(c => c.mail).filter(mail => mail && mail.includes("@"));
      const hasPeriodesAvecDates = safePeriodes.some(p => p.start && p.end);
      const dominantPeriodes = getDominantPeriodes(safePeriodes, dominantKeys);

      logDev("[Sondage] dominantPeriodes enrichis", dominantPeriodes);
      const plusieursTableaux = safePeriodes.length > 1;


      return (
        <div 
        className="sondage-container" 
        style={{ 
          display: "flex", 
          justifyContent: "center", 
          padding: "0 16px" 
          }}
          >          
            
            <Paper
            sx={{         
              backgroundColor: "#f4ece2" ,
              width: "100%",
              padding: 2,
              mt: isMobile ? 2 : `${headerHeight + bottomNavHeight + 16}px`,
              }}
            >

            {hasVotes && (
              <ChoixPeriodeDominante
              isAdmin={isAdmin}
              dominantKeys={dominantPeriodes}
              periodePrivilegiee={periodePrivilegiee}
              setPeriodePrivilegiee={setPeriodePrivilegiee}
              />
            )}

            {/* Formulaire d'édition */}         
            {editionActive && isAdmin && (
              
              <FormulairePeriodes            
              groupeId={groupeId}
              initialPeriodes={safePeriodes}
              onPériodesValidées={() => {
                setEditionActive(false);
                onReloadPériodes();
                }}
              role={role}
              />
              )}

            {/* Bouton modifier Periodes */}
            {!editionActive && isAdmin && !isSurveyClosed && (

              <Box sx={{ textAlign: "center", mt: 2, mb: 2 }}>
                <BoutonModifierPeriodes onClick={() => setEditionActive(true)} />
                </Box>
              )}

            {/* Liste des périodes */}
            {!noPeriode && !editionActive && (             
              <Box           
              className="periode-scroll-container"        
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },   // 📱 mobile = vertical
                justifyContent: "flex-start",
                gap: "1rem",
                overflowX: { xs: "hidden", sm: "auto" },      // pas de scroll horizontal sur mobile
                overflowY: "hidden",
                padding: "1rem 0",
                scrollSnapType: "none"
                }}
                >

                  {safePeriodes.map((periode, i) => {

                    const formatDate = (dateStr) => formatDateFr(dateStr, t("locale"));
                    const start = formatDate(periode.start);
                    const end = formatDate(periode.end);
                    const key = periode._id;
                    logDev("[Sondage] rendu période", { key, periode });   
                    let isDominante = false;
                    let isChoisie = false;
 
                    if (hasVotes) {
                      if (periodePrivilegiee) {


            // Si l’admin a choisi, seule la période choisie est colorée
            isDominante = periodePrivilegiee === periode._id;
            isChoisie = isDominante;

            } else {
              // Sinon, on colore toutes les dominantes naturelles
              isDominante = dominantKeys.includes(periode._id);
            }
          }
          
          // ⭐⭐ NOUVEAU : cacher les périodes non-dominantes pour les utilisateurs
          const shouldHide =
            isSurveyClosed &&
            !isAdmin &&
            !dominantKeys.includes(periode._id);

          if (shouldHide) return null;

          const periodeTotalRow = {
             _id: `total-${periode._id}`,
            contact: "Total",
            [periode._id]: calculateTotal(contacts, periode._id),
            };
            logDev("total calculé pour période", periode._id, periodeTotalRow[periode._id]);
            logDev("[Sondage] periodeTotalRow construit", periodeTotalRow);

          const periodeColumns = [
            { field: "contact", 
              headerName: t('Contacts'), 
              width: 180, 
              minWidth: 100, 
              flex: 1, 
              cellClassName: "left-align-cell" },
              { 
              field: periode._id,
              headerName: `${t('NumberOf')}\n${t('Participants')}`,
              headerClassName: "multi-line-header",
              width: 120, 
              minWidth: 80, 
              maxWidth: 120, 
              editable: true, 
              cellClassName: "left-align-cell",

          // 🔹 Affiche 0 ou autre si la valeur est vide
            valueFormatter: (params) => {
              if (!params) return 0; // sécurité
                const val = params.value;
                  return val === null || val === undefined || val === "" ? "" : val;
                },

              // 🔹 Quand on clique, sélectionne le contenu (ex: "0")
              // donc taper "5" remplace directement le 0 → pas de "05"
                renderEditCell: (params) => (
                <GridEditInputCell
                  {...params}
                  inputProps={{
                   ...params.inputProps,
                  onFocus: (e) => e.target.select()
                  }}
                />
                ),
                renderCell: (params) => (
                <Box 
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: 0.5,
                    justifyContent: "space-between",
                    }}
                >
                <span>{params.value}</span>
                <EditIcon 
                  fontSize="inherit" 
                  sx={{ fontSize: "0.9rem", color: "#aaa" }}
                />
                </Box>
              ) 
            },
          ];
          const periodeRows = [...contacts, periodeTotalRow];

          logDev("prop envoyée à MenusJournalier", periodeTotalRow[periode._id]);

            return (
              <Box
                key={periode._id}
                className="periode-block"
                sx={{
                  flex: { xs: "1 1 auto", sm: "0 0 auto" },
                  maxWidth: { xs: "100%", sm: 290 },
                  width: { xs: "100%", sm: 280 },
                  minWidth: { xs: "100%", sm: 280 },
                  boxSizing: "border-box",
                  padding: 1,

                  // ⭐⭐ LE STYLE MOUSSEUX autours de la peridoe
                  border: isDominante ? `4px solid ${couleur}35` : "2px solid #ddd",
                  boxShadow: isDominante 
                    ? `
                      0 0 10px ${couleur}55,
                      0 0 20px ${couleur}33,
                      inset 0 0 12px ${couleur}22
                    `
                    : "none",
                  borderRadius: 3,
                  background: isDominante
                    ? `linear-gradient(145deg, #ffffff, ${couleur}15)`
                    : "#fff",
                  backdropFilter: isDominante ? "blur(3px)" : "none",
                  
                  maxHeight: 650, // 👈 fixe la hauteur du bloc
                  overflowY: "auto", // 👈 active le scroll interne
                  // ⭐⭐ NOUVEAU : décalage uniquement si plusieurs tableaux et sur mobile
                  /*marginLeft: {
                    xs: plusieursTableaux && i === 0 ? "-40px" : "0px",
                    sm: "0px"
                  }*/
                 ml:0
                }}
              >

              {/* 👉 TON ACCORDION COMMENCE ICI */}
              <Accordion sx={{ backgroundColor: "#fff" }}>

              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                {/* Ligne 1 : date */}              
                <Typography sx={{ fontWeight: "bold" }}>
                  {start} → {end}
                  </Typography>

                {/* Ligne 2 : statut */}
                <Typography sx={{ color: isSurveyClosed ? "red" : "green" }}>
                  {isSurveyClosed ? t("SurveyCompleted"): t("SurveyOpen") }
                  </Typography>

                {/* Ligne 3 : nombre de personnes */}
                <Typography>
                  {t("TotalVotes")}{periodeTotalRow[periode._id]} 
                  </Typography>
              </Box>
            </AccordionSummary>

         
            <AccordionDetails>
              <Box sx={{ width: "auto", maxWidth: 270 }}>
                <TableauSondage               
                contacts={periodeRows}
                periodeId={periode._id}
                columns={periodeColumns}
                processRowUpdate={processRowUpdate}
                isSurveyClosed={!!periodeValidee}
                isAdmin={isAdmin}
                groupeActif={groupeActif}
                onContactCreated={rechargerContacts}
                totalVotes={periodeTotalRow[periode._id]}
                hasVotes={hasVotes} 
                nombrePeriodes={safePeriodes.length}
                onProcessRowUpdateError={(err) => console.error("Erreur DataGrid :", err)}
                editMode="cell"                     // 👈 active l’édition cellule
                experimentalFeatures={{ newEditingApi: true }}
                disableSelectionOnClick              // 👈 évite que le clic sélectionne seulement
                onCellClick={(params, event) => {
                // force l’édition dès le premier clic
                event.defaultMuiPrevented = true;
                }}
              />    
              </Box>
            </AccordionDetails>
            </Accordion>
          </Box>
        );
      })}
    </Box>
  )}

  {isAdmin && !groupeActif?.sondageVerrouille && !noPeriode && !editionActive && contacts.length > 0 && (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, mt: 3, mb: 8}}>
      {groupeActif?.lienWhatsApp && (
        <BoutonPartagerSondageWhatsApp
        groupeActif={groupeActif}
        nomGroupe={nomGroupe}
        t={t}
        setSnackbar={setSnackbar}
        />
      )}
      
        <BoutonEnvoyerEmailSondage
        groupeId={groupeId}
        destinataires={destinataires}
        nomGroupe={nomGroupe}
        snackbar={snackbar}
        setSnackbar={setSnackbar}
        sondageVerrouille={groupeActif?.sondageVerrouille}
        />

        {isMobile ? (       
          <BoutonEnvoyerSMSParticipants
          contacts={contactsDominante?.length > 0 ? contactsDominante : contacts}
          nomGroupe={nomGroupe}
          setSnackbar={setSnackbar}
          />         
          ) : (

            <Typography           
            variant="body2"
            sx={{
              fontStyle: "italic",
              color: "#999",
              temt: 1
            }}
            >

              {/*📱 Aller sur le mobile pour envoyer un SMS aux participants*/}
              </Typography>
            )}
    </Box>
  )}
       
  {!noPeriode && !editionActive &&  (

    <> 

    <Box    
    sx={{
      display: "flex", 
      flexDirection: { xs: "column", sm: "row" }, // 👈 colonne sur mobile, ligne sur desktop
      justifyContent: "center", 
      alignItems: "center", 
      gap: 1,          // espace horizontal entre les boutons
      mt: 2, 
      width: "100%",
      textAlign: "center",
      mb: 2, // 👈 marge entre validation et boutons mail/whatsapp
      }}
     >


      {isAdmin && !groupeActif?.sondageVerrouille && contacts.length > 0 && hasVotes && (

        <BoutonValiderEtVerrouiller
          groupeId={groupeId}
          periodePrivilegiee={periodePrivilegiee}
          periodeDominanteEffective={periodeDominanteEffective}
          setPeriodeValidee={setPeriodeValidee}
          setGroupeActif={setGroupeActif}
          onReloadPériodes={onReloadPériodes}
          setSnackbar={setSnackbar}
          t={t}
          couleur={couleur}
          emoji={emoji}
          safePeriodes={safePeriodes}
          setReloadTrigger={setReloadTrigger}
          langue={locale}
        />    
      )}

        <Button       
        variant="outlined"
        onClick={() => navigate("/menus")}
        sx={{
          backgroundColor: "#3399FF",
          color: "#fff",
          borderColor: "#3399FF",
          fontWeight: "bold",
            '&:hover': {
            backgroundColor: "#3399FF",
            color: "#fff"
          }
        }}
        >
           🍽️ {t("GoToMenus")}
        </Button>


        {isAdmin && !isSurveyClosed && (
          <Box sx={{ textAlign: "center", mt: 4 }}>
          </Box>
          )}

          </Box>
          </>
          )}

          {/* Boutons WhatsApp & Email */}
          {isAdmin && !groupeActif?.sondageVerrouille && !noPeriode && !editionActive && contacts.length > 0 && hasVotes && (
            
            <Box          
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "flex-start",
              gap: 2,
              flexWrap: "wrap",
              mt: 2,
              mb: 4
              }}
            >


              {/* 📧 Copier les emails */}
              <BoutonCopierTousEmails 
              emails={emailsPeriodeDominante} 
              sx={{ alignSelf: "center" }} // 👈 force l’alignement vertical
              />

              {/* 📱 Créer / inviter au groupe WhatsApp */}
              {isMobile ? (

                <BoutonWhatsApp 
                nomGroupe={nomGroupe} 
                contacts={contactsDominante} 
                t={t} 
                sx={{ alignSelf: "center" }}
              />

              ) : (

                <Typography
                variant="body2"
                sx={{
                  fontStyle: "italic",
                  color: "#999",
                  textAlign: "center",
                  mt: 1
                  }}
                >


                <TableauListeItems
                contacts={contactsDominante}
                nomGroupe={nomGroupe}
                t={t}
                />


              {/*📱 Aller sur le mobile pour créer le groupe WhatsApp*/}
              </Typography>
    )}

  </Box>
)}   

      </Paper>
      {/* Snackbar pour afficher les messages de succès/erreur */}

      <SnackbarFeedback 
        snackbar={snackbar}
        setSnackbar={setSnackbar}
        bottomNavHeight={bottomNavHeight}
        emoji={emoji}
        couleur={couleur}
      />
    </div>
  );
};

export default Sondage;